import React, { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  Send,
  X,
  Bot,
  User,
  MessageSquare,
  RefreshCw,
  ExternalLink,
} from "lucide-react";

const GHANDIVA_SYSTEM_INSTRUCTION = `
Anda adalah Customer Service Virtual AI yang ramah, profesional, dan solutif untuk "Ghandiva IT Solutions".
Tugas Anda adalah membantu pengunjung website memahami layanan Ghandiva, menjawab pertanyaan seputar konsultasi IT, estimasi pembuatan aplikasi/website, serta mengarahkan mereka untuk berkonsultasi langsung dengan tim kami jika diperlukan.

Informasi Utama Ghandiva:
1. Profil: Ghandiva adalah penyedia solusi teknologi digital terdepan yang membantu transformasi bisnis modern dengan pendekatan personal dan hasil terukur.
2. Layanan Utama:
   - ARJUNA: Pengembangan Website responsif & Web Application modern dengan performa tinggi (Fullstack, React, Next.js, Node.js, UI/UX).
   - SRIKANDI: Pengembangan Aplikasi Mobile (Android & iOS) native maupun cross-platform (Flutter, React Native) yang user-friendly.
   - PRAJURIT: Cloud Infrastructure, DevOps & Migration (GCP, AWS, VPS, Docker, CI/CD, optimasi performa dan keamanan tinggi).
3. Keunggulan:
   - 500+ Klien Puas, 99% Uptime, 24/7 Support, Rating 5 Bintang.
   - Konsultasi gratis untuk semua calon klien.
4. Informasi Kontak:
   - WhatsApp / Telepon: +62 89 5186 31300
   - Email: info@ghandiva.biz.id & support@ghandiva.biz.id
   - Alamat Kantor: Jl. Wonoayu No. 10, Sawunggaling, Kec Wonokromo, Surabaya, Jawa Timur 60242.
   - Jam Operasional: Senin - Jumat (09:00 - 18:00), Sabtu (09:00 - 15:00), Support 24/7 untuk klien.

Gaya Komunikasi:
- Gunakan bahasa Indonesia yang sopan, ramah, jelas, dan profesional.
- Berikan jawaban yang ringkas namun informatif (gunakan poin/bullet bila perlu).
- Selalu tawarkan bantuan lebih lanjut dan beritahu bahwa mereka dapat berkonsultasi gratis melalui WhatsApp atau halaman Kontak kami.
`;

const QUICK_QUESTIONS = [
  "Apa saja layanan di Ghandiva?",
  "Berapa estimasi biaya pembuatan website?",
  "Bagaimana proses konsultasi gratis?",
  "Apa keunggulan layanan ARJUNA & SRIKANDI?",
];

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "welcome-1",
      sender: "ai",
      text: "Halo! 👋 Selamat datang di **Ghandiva IT Solutions**. Saya asisten virtual AI yang siap membantu menjawab pertanyaan Anda seputar layanan kami, estimasi proyek, atau konsultasi gratis.",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Helper rule-based fallback response if Gemini key not set or network error
  const getFallbackResponse = (query) => {
    const q = query.toLowerCase();
    if (q.includes("layanan") || q.includes("service") || q.includes("produk")) {
      return `Ghandiva menyediakan 3 layanan utama:\n\n1. **ARJUNA** (Web Development): Pembuatan website responsif & aplikasi web modern dengan performa tinggi.\n2. **SRIKANDI** (Mobile App Development): Aplikasi Android & iOS yang intuitif dan user-friendly.\n3. **PRAJURIT** (Cloud & DevOps): Migrasi cloud, manajemen server, dan optimasi infrastruktur yang aman & skalabel.\n\nApakah Anda ingin konsultasi gratis untuk salah satu layanan di atas?`;
    }
    if (q.includes("biaya") || q.includes("harga") || q.includes("tarif") || q.includes("price")) {
      return `Biaya pengembangan di Ghandiva disesuaikan dengan skala, fitur, dan kompleksitas proyek Anda. Kami menyediakan **Konsultasi Gratis** untuk menghitung estimasi RAB yang paling efisien bagi bisnis Anda.\n\nSilakan klik tombol WhatsApp di bawah untuk diskusi langsung dengan tim konsultan kami!`;
    }
    if (q.includes("arjuna")) {
      return `**ARJUNA** adalah paket layanan pengembangan website dan web app dari Ghandiva. Mulai dari Company Profile, E-Commerce, Dashboard Admin, hingga SaaS dengan arsitektur modern (React, Next.js, Node.js) yang cepat dan SEO-friendly.`;
    }
    if (q.includes("srikandi")) {
      return `**SRIKANDI** adalah solusi pengembangan aplikasi mobile (Android & iOS). Kami membangun aplikasi native dan hybrid (Flutter/React Native) dengan tampilan menarik, performa gesit, dan keamanan data terjamin.`;
    }
    if (q.includes("prajurit")) {
      return `**PRAJURIT** berfokus pada infrastruktur Cloud & DevOps. Kami membantu migrasi server, deployment otomatis (CI/CD), monitoring 24/7, serta optimasi biaya cloud (GCP, AWS, VPS).`;
    }
    if (q.includes("kontak") || q.includes("hubungi") || q.includes("wa") || q.includes("whatsapp") || q.includes("telepon")) {
      return `Anda dapat menghubungi kami melalui:\n- **WhatsApp / Telp**: +62 89 5186 31300\n- **Email**: info@ghandiva.biz.id\n- **Kantor**: Jl. Wonoayu No. 10, Wonokromo, Surabaya.\n\nTim kami siap merespons kebutuhan Anda!`;
    }
    return `Terima kasih atas pertanyaannya! Ghandiva siap membantu kebutuhan transformasi digital dan teknologi IT bisnis Anda. Anda dapat mengajukan konsultasi gratis langsung ke tim konsultan kami via WhatsApp di **+62 89 5186 31300** atau melalui halaman Kontak.`;
  };

  const handleSendMessage = async (textToSend) => {
    const query = textToSend || inputText;
    if (!query.trim() || isLoading) return;

    const userMessageId = Date.now().toString();
    const newMessages = [
      ...messages,
      {
        id: userMessageId,
        sender: "user",
        text: query.trim(),
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    ];

    setMessages(newMessages);
    setInputText("");
    setIsLoading(true);

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";

    if (!apiKey) {
      // Use smart CS fallback engine with simulated natural delay
      setTimeout(() => {
        const fallbackText = getFallbackResponse(query);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            sender: "ai",
            text: fallbackText,
            timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          },
        ]);
        setIsLoading(false);
      }, 700);
      return;
    }

    try {
      // Build conversation contents for Gemini API
      const conversationHistory = newMessages.map((msg) => ({
        role: msg.sender === "user" ? "user" : "model",
        parts: [{ text: msg.text }],
      }));

      // Direct call to Gemini 1.5 Flash / 2.0 endpoint
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            systemInstruction: {
              parts: [{ text: GHANDIVA_SYSTEM_INSTRUCTION }],
            },
            contents: conversationHistory,
            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 600,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Gemini API Error: ${response.statusText}`);
      }

      const data = await response.json();
      const aiReply =
        data?.candidates?.[0]?.content?.parts?.[0]?.text ||
        getFallbackResponse(query);

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text: aiReply,
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    } catch (err) {
      console.warn("API request failed, using CS fallback:", err);
      const fallbackText = getFallbackResponse(query);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          sender: "ai",
          text: fallbackText,
          timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: "welcome-1",
        sender: "ai",
        text: "Halo! 👋 Percakapan telah direset. Ada yang bisa kami bantu seputar layanan IT Ghandiva?",
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    ]);
  };

  // Format simple markdown into bold and line breaks
  const renderFormattedText = (text) => {
    const lines = text.split("\n");
    return lines.map((line, idx) => {
      // Process bold **text**
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <div key={idx} className={line.trim() === "" ? "h-2" : "min-h-[1.25rem]"}>
          {parts.map((part, pIdx) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return (
                <strong key={pIdx} className="font-semibold text-gray-900">
                  {part.slice(2, -2)}
                </strong>
              );
            }
            return <span key={pIdx}>{part}</span>;
          })}
        </div>
      );
    });
  };

  return (
    <>
      {/* Floating Launcher Button */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center">
        {!isOpen && (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="group relative flex items-center gap-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-800 text-white px-5 py-3.5 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300 border border-blue-400/30 focus:outline-none focus:ring-4 focus:ring-blue-500/30"
            aria-label="Buka Chat Tanya AI"
          >
            {/* Pulse Indicator */}
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>

            <Sparkles className="w-5 h-5 animate-pulse text-amber-300" />
            <span className="font-semibold text-sm tracking-wide">Tanya AI CS</span>
          </button>
        )}
      </div>

      {/* Chat Window Dialog */}
      {isOpen && (
        <div className="fixed bottom-6 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[410px] h-[580px] max-h-[85vh] bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 text-white p-4 px-5 flex items-center justify-between border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center shadow-inner">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-gray-900 rounded-full"></span>
              </div>
              <div>
                <h3 className="font-bold text-sm tracking-wide text-white">
                  Ghandiva AI CS
                </h3>
                <p className="text-xs text-gray-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse"></span>
                  Online • Siap Membantu 24/7
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleClearChat}
                title="Reset Percakapan"
                className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800/60 rounded-xl transition"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                title="Tutup Chat"
                className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800/60 rounded-xl transition ml-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Chat Messages Body */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50/50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2.5 ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {msg.sender === "ai" && (
                  <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                    <Bot className="w-4 h-4" />
                  </div>
                )}

                <div
                  className={`max-w-[82%] rounded-2xl p-3.5 text-xs sm:text-sm leading-relaxed ${
                    msg.sender === "user"
                      ? "bg-blue-600 text-white rounded-tr-sm shadow-md"
                      : "bg-white text-gray-800 border border-gray-100 rounded-tl-sm shadow-sm"
                  }`}
                >
                  <div>{renderFormattedText(msg.text)}</div>
                  <div
                    className={`text-[10px] mt-1.5 text-right ${
                      msg.sender === "user" ? "text-blue-200" : "text-gray-400"
                    }`}
                  >
                    {msg.timestamp}
                  </div>
                </div>

                {msg.sender === "user" && (
                  <div className="w-8 h-8 rounded-xl bg-gray-800 text-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2.5 justify-start">
                <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm p-3.5 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Action Suggestion Chips */}
          <div className="px-4 py-2 bg-white border-t border-gray-100 flex gap-2 overflow-x-auto no-scrollbar">
            {QUICK_QUESTIONS.map((q, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSendMessage(q)}
                disabled={isLoading}
                className="whitespace-nowrap text-[11px] bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-700 px-3 py-1.5 rounded-full border border-gray-200/80 transition-all flex-shrink-0"
              >
                {q}
              </button>
            ))}
          </div>

          {/* WhatsApp Direct Escalation Footer Link */}
          <div className="px-4 py-1.5 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between text-[11px] text-emerald-800">
            <span className="flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
              Ingin respon cepat dari CS manusia?
            </span>
            <a
              href="https://wa.me/6289518631300?text=Halo%20Ghandiva%2C%20saya%20ingin%20konsultasi%20layanan%20IT"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 hover:underline"
            >
              WhatsApp <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Input Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="p-3 bg-white border-t border-gray-200 flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ketik pertanyaan untuk CS AI..."
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
            />
            <button
              type="submit"
              disabled={!inputText.trim() || isLoading}
              className="w-10 h-10 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-600 text-white flex items-center justify-center transition shadow-md active:scale-95 flex-shrink-0"
              aria-label="Kirim Pesan"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

